import React, { useState, useEffect, useCallback } from 'react';
import { assetAPI, userAPI } from '../../../services';
import { Package, Plus, CheckCircle, RotateCcw, AlertCircle, X, RefreshCw, Edit2 } from 'lucide-react';

const ASSET_TYPES = ['laptop','mobile','tablet','monitor','keyboard','mouse','headset','sim_card','vehicle','access_card','other'];
const TYPE_LABELS = { laptop:'Laptop', mobile:'Mobile', tablet:'Tablet', monitor:'Monitor', keyboard:'Keyboard', mouse:'Mouse', headset:'Headset', sim_card:'SIM Card', vehicle:'Vehicle', access_card:'Access Card', other:'Other' };
const STATUS_COLORS = { available:'bg-green-100 text-green-700', assigned:'bg-blue-100 text-blue-700', returned:'bg-gray-100 text-gray-600', damaged:'bg-red-100 text-red-700', lost:'bg-red-200 text-red-900' };
const CONDITIONS = ['new','good','fair','poor'];

const AssetsPage = () => {
  const [assets, setAssets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showAssign, setShowAssign] = useState(null);
  const [showReturn, setShowReturn] = useState(null);
  const [form, setForm] = useState({ name:'', assetType:'laptop', serialNumber:'', description:'', condition:'good', notes:'' });
  const [assignForm, setAssignForm] = useState({ employeeId:'', notes:'' });
  const [returnForm, setReturnForm] = useState({ condition:'good', notes:'' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [empSearch, setEmpSearch] = useState('');

  const showToast = (msg, type='success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [ar, sr] = await Promise.all([assetAPI.getAssets(), assetAPI.getAssetStats()]);
      setAssets(Array.isArray(ar?.data) ? ar.data : []);
      setStats(sr?.data || null);
    } catch { showToast('Failed to load', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    userAPI.getUsers({ active: true, limit: 500 })
      .then(r => setEmployees(r?.data?.users || []))
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!form.name.trim()) return showToast('Name is required', 'error');
    try {
      setSaving(true);
      if (editId) await assetAPI.updateAsset(editId, form);
      else await assetAPI.createAsset(form);
      showToast(editId ? 'Asset updated' : 'Asset created');
      setShowForm(false); load();
    } catch { showToast('Save failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleAssign = async () => {
    if (!assignForm.employeeId.trim()) return showToast('Employee ID required', 'error');
    try {
      setSaving(true);
      await assetAPI.assignAsset(showAssign._id, assignForm);
      showToast('Asset assigned'); setShowAssign(null); load();
    } catch { showToast('Assign failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleReturn = async () => {
    try {
      setSaving(true);
      await assetAPI.returnAsset(showReturn._id, returnForm);
      showToast('Asset returned'); setShowReturn(null); load();
    } catch { showToast('Return failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this asset?')) return;
    try { await assetAPI.deleteAsset(id); showToast('Deleted'); load(); }
    catch { showToast('Failed', 'error'); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {toast && <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type==='error'?'bg-red-500':'bg-green-500'}`}>{toast.msg}</div>}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Package className="w-6 h-6 text-blue-600"/>Asset Management</h1>
          <p className="text-sm text-gray-500 mt-1">Track and manage company assets assigned to employees</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"><RefreshCw className="w-4 h-4"/></button>
          <button onClick={() => { setForm({ name:'', assetType:'laptop', serialNumber:'', description:'', condition:'good', notes:'' }); setEditId(null); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"><Plus className="w-4 h-4"/>Add Asset</button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { l:'Total', v:stats.total, c:'text-gray-900', b:'bg-gray-50' },
            { l:'Available', v:stats.available, c:'text-green-700', b:'bg-green-50' },
            { l:'Assigned', v:stats.assigned, c:'text-blue-700', b:'bg-blue-50' },
            { l:'Returned', v:stats.returned, c:'text-gray-600', b:'bg-gray-100' }
          ].map(({ l, v, c, b }) => (
            <div key={l} className={`${b} rounded-xl p-4 text-center`}>
              <p className={`text-2xl font-bold ${c}`}>{v}</p>
              <p className="text-xs text-gray-500 mt-0.5">{l}</p>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? <div className="py-10 text-center text-gray-400">Loading...</div>
        : assets.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-gray-400">
            <Package className="w-12 h-12 mb-3 opacity-30"/><p>No assets yet. Add your first asset.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>{['Asset','Type','Serial No.','Condition','Status','Assigned To','Actions'].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {assets.map(a => (
                <tr key={a._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3"><p className="font-medium text-gray-900">{a.name}</p>{a.description && <p className="text-xs text-gray-400">{a.description}</p>}</td>
                  <td className="px-4 py-3 text-gray-600">{TYPE_LABELS[a.assetType] || a.assetType}</td>
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs">{a.serialNumber || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 capitalize">{a.condition}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[a.status]}`}>{a.status}</span></td>
                  <td className="px-4 py-3">{a.assignedTo ? <div><p className="font-medium text-gray-900">{a.assignedTo.name}</p><p className="text-xs text-gray-400">{a.assignedTo.employeeId}</p></div> : <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => { setForm({ name:a.name, assetType:a.assetType, serialNumber:a.serialNumber||'', description:a.description||'', condition:a.condition, notes:a.notes||'' }); setEditId(a._id); setShowForm(true); }} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-3 h-3"/></button>
                      {a.status === 'available' && <button onClick={() => { setShowAssign(a); setAssignForm({ employeeId:'', notes:'' }); setEmpSearch(''); }} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Assign"><CheckCircle className="w-3 h-3"/></button>}
                      {a.status === 'assigned' && <button onClick={() => { setShowReturn(a); setReturnForm({ condition:'good', notes:'' }); }} className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded" title="Return"><RotateCcw className="w-3 h-3"/></button>}
                      <button onClick={() => handleDelete(a._id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><AlertCircle className="w-3 h-3"/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">{editId?'Edit Asset':'Add Asset'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 space-y-3">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. MacBook Pro 14"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={form.assetType} onChange={e=>setForm(f=>({...f,assetType:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {ASSET_TYPES.map(t=><option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
                  </select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                  <select value={form.condition} onChange={e=>setForm(f=>({...f,condition:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {CONDITIONS.map(c=><option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                  </select></div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                <input value={form.serialNumber} onChange={e=>setForm(f=>({...f,serialNumber:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="SN-XXXX"/></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"/></div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60">{saving?'Saving...':'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssign && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
            <h2 className="font-semibold text-gray-900 mb-4">Assign: {showAssign.name}</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
                <input
                  value={empSearch}
                  onChange={e => { setEmpSearch(e.target.value); setAssignForm(f => ({ ...f, employeeId: '' })); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-1"
                  placeholder="Search by name or ID..."
                />
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                  {employees
                    .filter(emp => {
                      const q = empSearch.toLowerCase();
                      return !q || (emp.name || '').toLowerCase().includes(q) || (emp.employeeId || '').toLowerCase().includes(q);
                    })
                    .map(emp => (
                      <button
                        key={emp._id}
                        type="button"
                        onClick={() => { setAssignForm(f => ({ ...f, employeeId: emp._id })); setEmpSearch(`${emp.name} (${emp.employeeId})`); }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-green-50 flex justify-between items-center ${assignForm.employeeId === emp._id ? 'bg-green-50 font-medium' : ''}`}
                      >
                        <span>{emp.name}</span>
                        <span className="text-xs text-gray-400">{emp.employeeId}</span>
                      </button>
                    ))}
                  {employees.filter(emp => {
                    const q = empSearch.toLowerCase();
                    return !q || (emp.name || '').toLowerCase().includes(q) || (emp.employeeId || '').toLowerCase().includes(q);
                  }).length === 0 && <p className="px-3 py-2 text-sm text-gray-400">No employees found</p>}
                </div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <input value={assignForm.notes} onChange={e=>setAssignForm(f=>({...f,notes:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"/></div>
              <div className="flex gap-3"><button onClick={() => { setShowAssign(null); setEmpSearch(''); }} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                <button onClick={handleAssign} disabled={saving || !assignForm.employeeId} className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium disabled:opacity-60">Assign</button></div>
            </div>
          </div>
        </div>
      )}

      {/* Return Modal */}
      {showReturn && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
            <h2 className="font-semibold text-gray-900 mb-4">Return: {showReturn.name}</h2>
            <div className="space-y-3">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Returned Condition</label>
                <select value={returnForm.condition} onChange={e=>setReturnForm(f=>({...f,condition:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  {CONDITIONS.map(c=><option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                </select></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <input value={returnForm.notes} onChange={e=>setReturnForm(f=>({...f,notes:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"/></div>
              <div className="flex gap-3"><button onClick={() => setShowReturn(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                <button onClick={handleReturn} disabled={saving} className="flex-1 py-2 bg-yellow-600 text-white rounded-lg text-sm font-medium disabled:opacity-60">Mark Returned</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetsPage;
